import type { GraphQLErrorPayload as GraphQLErrorInterop } from '@confluence/relay-errors/entry-points/RelayError.ts';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { QueryInteropFetchPolicy } from '@confluence/apollo-relay-interop/entry-points/mapFetchPolicyForQuery.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { preloadLocalStorage } from '@confluence/preload-storage-queries/entry-points/preloadLocalStorage.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import BlogTreeRootComponentQuery, {
	type BlogTreeRootComponentQuery$data,
	type BlogTreeRootComponentQuery$variables,
} from './__generated__/BlogTreeRootComponentQuery.graphql.ts';
import { PAGE_SIZE, BLOG_STATUSES } from './treeParameters.js';

export async function isBlogTreeExpanded(): Promise<boolean> {
	try {
		const entries = await preloadLocalStorage();
		const sideNavExpandState = entries.find((entry) =>
			entry.key.endsWith(PERSISTED_KEYS_ON_SERVER.PERSISTED_SPACE_NAV_SECTIONS_EXPANDED_STATES),
		);
		if (sideNavExpandState.value) {
			const expandedStates = JSON.parse(sideNavExpandState.value);
			if (expandedStates.blogTree) {
				return true;
			}
		}
	} catch {
		// Return value might be malformed
	}
	return false;
}

export const preloadBlogTree = async ({
	spaceKey,
	fetchPolicy,
}: {
	spaceKey: string;
	fetchPolicy?: QueryInteropFetchPolicy;
}): Promise<
	| {
			data: BlogTreeRootComponentQuery$data | null;
			errors?: ReadonlyArray<GraphQLErrorInterop>;
			hasErrors: boolean;
	  }
	| undefined
> => {
	// blog tree is default collapsed, and is only expanded when user lands directly on a blog.
	// Or use explicity expanded the blog tree section
	const blogTreeQueryPromise = query<
		BlogTreeRootComponentQuery$data,
		BlogTreeRootComponentQuery$variables
	>({
		query: BlogTreeRootComponentQuery,
		...(fetchPolicy && { fetchPolicy }),
		variables: {
			spaceKey,
			first: PAGE_SIZE,
			statuses: BLOG_STATUSES,
			orderBy: 'metadata.createdDate desc',
		},
	});
	// isBlogTreeExpanded() is much faster then blogTreeQueryPromise
	// If section is not expanded we don't wait for the blog tree query to finish
	return isBlogTreeExpanded().then((expanded) => {
		if (expanded) {
			return blogTreeQueryPromise;
		}
	});
};
